
import { useState, useEffect } from "react"
import { Modal, Button, Spinner } from "react-bootstrap"
import Select from "react-select"
import { useDispatch, useSelector } from "react-redux"
import swal from "sweetalert"
import {
  getPatientMaleChartAction,
  createPatientMaleChartAction,
  updatePatientMaleChartAction,
  deletePatientMaleChartAction,
} from "../../../../../store/actions/PatientMalechartAction"
import { sendGenderFormLinkAction } from "../../../../../store/actions/PatientLinkAction"
import { handleSuccess, showErrorAlert } from "../../../../../services/ErrorHandlingService"
import { formatDate } from "../../../../../utils/dateUtils"
const PatientMale = () => {
  const dispatch = useDispatch()

  // Redux selectors
  const prescriptionPatientDetails = useSelector(
    (state) => state.prescriptionState?.patientDetails
  );
  const patientDetails = useSelector((state) => state.patients?.patientDetails)
  const patientMaleCharts = useSelector((state) => state.patientMaleChart?.patientMaleCharts || [])
  const patient_id = prescriptionPatientDetails?.patient_id || patientDetails?.patient_id;
  const loading = useSelector((state) => state.patientMaleChart?.loading || false)
  const error = useSelector((state) => state.patientMaleChart?.error)

  // Local state
  const [showModal, setShowModal] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [currentEditIndex, setCurrentEditIndex] = useState(-1)
  const [currentEditChartId, setCurrentEditChartId] = useState(null)
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sending, setSending] = useState(false)
  const [formData, setFormData] = useState({
    date: "",
    symptoms: null,
    otherSymptom: "",
    metallicColor: null,
  })

  // Fetch patient male charts on component mount
  useEffect(() => {
    if (patient_id) {
      dispatch(
        getPatientMaleChartAction({
          patient_id
        }),
      )
    }
  }, [dispatch, patient_id])

  // Transform API data for table display
  const [tableDates, setTableDates] = useState([])
  const [tableRows, setTableRows] = useState([
    {
      label: "Symptoms during/after intercourse?",
      values: [],
    },
    {
      label: "Metallic color",
      values: [],
    },
  ])

  // Update table data when Redux state changes
  useEffect(() => {
    if (patientMaleCharts && patientMaleCharts.length > 0) {
      const dates = patientMaleCharts.map((chart) => chart.date || { day: "", year: "" })
      const symptomsValues = patientMaleCharts.map((chart) => chart.symptoms || "")
      const colorValues = patientMaleCharts.map((chart) => chart.metallicColor || "")

      setTableDates(dates)
      setTableRows([
        {
          label: "Symptoms during/after intercourse?",
          values: symptomsValues,
        },
        {
          label: "Metallic color",
          values: colorValues,
        },
      ])
    } else {
      setTableDates([])
      setTableRows([
        {
          label: "Symptoms during/after intercourse?",
          values: [],
        },
        {
          label: "Metallic color",
          values: [],
        },
      ])
    }
  }, [patientMaleCharts])

  const handleModalClose = () => {
    setShowModal(false)
    resetForm()
    setErrors({})
  }

  const handleAddClick = () => {
    setIsEditMode(false)
    setCurrentEditIndex(-1)
    setCurrentEditChartId(null)
    resetForm()
    setShowModal(true)
  }

  const handleEditClick = (index) => {
    if (!patientMaleCharts[index]) return

    setIsEditMode(true)
    setCurrentEditIndex(index)
    setCurrentEditChartId(patientMaleCharts[index].chart_id)

    const chart = patientMaleCharts[index]

    // Parse symptoms - they come as comma-separated from formatSymptomsField
    const symptomValues = chart.symptoms ? chart.symptoms.split(", ") : []

    // Check if there's a custom symptom (not in the predefined list)
    const customSymptoms = symptomValues.filter(
      (symptom) => !symptomsList.includes(symptom) && symptom !== "None of the above",
    )

    let otherSymptom = ""
    let filteredSymptoms = [...symptomValues]

    if (customSymptoms.length > 0) {
      // Use the first custom symptom as "other"
      otherSymptom = customSymptoms[0]

      // Filter out custom symptoms and add "Other" option
      filteredSymptoms = symptomValues.filter(
        (symptom) => symptomsList.includes(symptom) || symptom === "None of the above",
      )
      filteredSymptoms.push("Other")
    }

    const symptomOptions = filteredSymptoms
      .map((symptom) => symptomsOptions.find((option) => option.label === symptom))
      .filter(Boolean)

    // Find matching color option
    const colorOption = metallicColorOptions.find((option) => option.label === chart.metallicColor) || null

    const dateStr = formatDateForInput(chart.date)

    setFormData({
      date: dateStr,
      symptoms: symptomOptions.length > 0 ? symptomOptions : null,
      otherSymptom: otherSymptom,
      metallicColor: colorOption,
    })

    setShowModal(true)
  }

  const handleDeleteClick = async (index) => {
    if (!patientMaleCharts[index]) return

    const result = await swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this chart entry!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })

    if (result) {
      try {
        await dispatch(
          deletePatientMaleChartAction({
            chart_id: patientMaleCharts[index].chart_id,
            patient_id: patientDetails.patient_id,
          }),
        )
        handleSuccess("Chart entry deleted successfully!", "success")
      } catch (error) {
        console.error("Delete error:", error)
        showErrorAlert("Failed to delete chart entry", "error")
      }
    }
  }

  // Helper function to format date for input field
  const formatDateForInput = (dateObj) => {
    if (!dateObj || !dateObj.day || !dateObj.year) return ""

    try {
      // Parse the date object format like { day: "12 Feb", year: "2025" }
      const dayPart = dateObj.day.split(" ")
      if (dayPart.length === 2) {
        const day = dayPart[0]
        const month = getMonthNumber(dayPart[1])
        const year = dateObj.year
        return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`
      }
      return ""
    } catch (e) {
      console.error("Error formatting date:", e)
      return ""
    }
  }

  // Helper function to convert month name to number
  const getMonthNumber = (monthName) => {
    const months = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    }
    return months[monthName] || 1
  }

  const resetForm = () => {
    setFormData({
      date: "",
      symptoms: null,
      otherSymptom: "",
      metallicColor: null,
    })
    setErrors({})
  }

  const symptomsList = [
    "Decreased duration of intercourse",
    "Reduced erection hardness",
    "Semen has become watery",
    "Decrease in semen volume",
    "Lack of sexual desire",
    "Increased fatigue",
    "Body aches",
    "Burning sensation or heat in the genital area",
    "Lumps felt in semen",
    "Blood present in semen",
    "Foul or decayed smell from semen",
    "Pus discharge in semen",
    "Pain during or after intercourse",
    "Burning sensation in the genitals",
    "Rashes or eruptions on the genitals",
    "Cracks or fissures on the genitals",
    "None of the above",
    "Other",
  ]

  const metallicColors = ["White", "Gray", "Yellow"]

  const symptomsOptions = symptomsList.map((symptom) => ({
    value: symptom,
    label: symptom,
  }))

  const metallicColorOptions = metallicColors.map((color) => ({
    value: color,
    label: color,
  }))

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const handleSelectChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta
    setFormData((prev) => ({ ...prev, [name]: selectedOption }))

    // Clear error when user makes selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.date.trim()) {
      newErrors.date = "Date is required"
    }

    if (!formData.symptoms || formData.symptoms.length === 0) {
      newErrors.symptoms = "At least one symptom is required"
    }

    if (!formData.metallicColor) {
      newErrors.metallicColor = "Metallic color is required"
    }

    // Validate other symptom if "Other" is selected
    if (formData.symptoms?.some((option) => option.value === "Other") && !formData.otherSymptom.trim()) {
      newErrors.otherSymptom = "Please specify the other symptom"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    if (isSubmitting) {
      console.log("Already submitting, please wait...")
      return
    }

    setIsSubmitting(true)

    try {
      const chartData = {
        patient_id: patientDetails.patient_id,
        date: formData.date,
        symptoms: formData.symptoms,
        otherSymptom: formData.otherSymptom,
        metallicColor: formData.metallicColor,
      }

      if (isEditMode && currentEditChartId) {
        // Update existing chart
        chartData.chart_id = currentEditChartId

        const result = await dispatch(updatePatientMaleChartAction(chartData))

        if (result.success) {
          handleSuccess("Chart updated successfully!", "success")
          handleModalClose()
        }
      } else {
        // Create new chart
        const result = await dispatch(createPatientMaleChartAction(chartData))

        if (result.success) {
          handleSuccess("Chart created successfully!", "success")
          handleModalClose()
        }
      }
    } catch (error) {
      console.error("Submit error:", error)
      const errorMessage = error.response?.data?.message || error.message || "Failed to save chart"
      showErrorAlert(errorMessage, "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card shadow-none">
          <div className="card-body">
            <div className="text-end mb-3 d-flex gap-2 justify-content-end">
              {/* Add button */}
              {/* <button className="btn btn-primary" onClick={handleAddClick} disabled={loading}>
                {loading ? "Loading..." : "Add"}
              </button> */}
              <Button
                variant="outline-info"
                size="sm"
                onClick={handleAddClick}
                disabled={loading}
              >
                {loading ? "Loading..." : "Add"}
              </Button>


              {/* Send Link button */}
              {/* <button
                className="btn btn-primary"
                onClick={() => {
                  if (!patient_id) {
                    showErrorAlert("Patient ID not found!");
                    return;
                  }
                  dispatch(sendGenderFormLinkAction(patient_id, "MALE"));
                }}
              >
                Send Link
              </button> */}
              {/* <Button
                variant="primary"
                className="btn btn-primary"
                disabled={sending} // ✅ disable while loading
                onClick={async () => {
                  if (!patient_id) {
                    showErrorAlert("Patient ID not found!");
                    return;
                  }
                  try {
                    setSending(true); // 🔄 start loading
                    await dispatch(sendGenderFormLinkAction(patient_id, "MALE"));
                    //handleSuccess("Link sent successfully!");
                  } catch (err) {
                    console.error(err);
                    showErrorAlert("Failed to send link");
                  } finally {
                    setSending(false); // ⏹ stop loading
                  }
                }}
              >
                {sending ? "Sending..." : "Send Link"}
              </Button> */}
              <Button
                variant="outline-primary"
                size="sm"
                disabled={sending}   // disable while loading
                onClick={async () => {
                  if (!patient_id) {
                    showErrorAlert("Patient ID not found!");
                    return;
                  }
                  try {
                    setSending(true); // start loader
                    await dispatch(sendGenderFormLinkAction(patient_id, "MALE"));
                    //handleSuccess("Link sent successfully!");
                  } catch (err) {
                    console.error(err);
                    showErrorAlert("Failed to send link");
                  } finally {
                    setSending(false); // stop loader
                  }
                }}
              >
                {sending ? "Sending..." : "Send Link"}
              </Button>

            </div>


            {loading && (
              <div className="text-center mb-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}

            {error && <div className="alert alert-danger mb-3">{error}</div>}

            <div className="table-responsive">
              <div id="patient" className="dataTables_wrapper no-footer">
                <table
                  className="table header-border"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    border: "2px solid #ddd",
                  }}
                >
                  <thead>
                    <tr className="table-info">
                      <th
                        scope="col"
                        style={{
                          padding: "10px",
                          textAlign: "center",
                        }}
                      ></th>

                      {tableDates.map((date, idx) => (
                        <th
                          key={idx}
                          scope="col"
                          style={{
                            color: "black",
                            fontWeight: "bold",
                            padding: "10px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <div>
                            <div>
                              {formatDate(`${date.day} ${date.year}`)}
                            </div>


                            <div className="mt-2 d-flex justify-content-center gap-3">
                              <i
                                className="fa fa-pencil-alt text-success"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleEditClick(idx)}
                              />
                              <i
                                className="fa fa-trash text-danger"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleDeleteClick(idx)}
                              />
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {patientMaleCharts.length === 0 ? (
                      <tr>
                        <td colSpan={tableDates.length + 1} className="text-center py-4">
                          No records available
                        </td>
                      </tr>
                    ) : (
                      tableRows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          <th
                            scope="row"
                            style={{
                              color: "black",
                              fontWeight: "normal",
                              padding: "10px",
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            {row.label}
                          </th>
                          {row.values.map((value, colIndex) => (
                            <td
                              key={colIndex}
                              style={{ padding: "10px", textAlign: "center", verticalAlign: "middle" }}
                            >
                              {value}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
              <Modal.Header closeButton>
                <Modal.Title>{isEditMode ? "Edit Info" : "Add Info"}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form onSubmit={handleSubmit}>
                  {/* Date Input */}
                  <div className="mb-3">
                    <label className="form-label">
                      Date <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type="date"
                        className="form-control pe-5"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        max={new Date().toISOString().split("T")[0]}
                      />
                      <i
                        className="fa fa-calendar position-absolute"
                        style={{
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          fontSize: "18px",
                          color: "#6c757d",
                        }}
                      />
                    </div>
                    {errors.date && <small className="text-danger">{errors.date}</small>}
                  </div>

                  {/* Symptoms Dropdown */}
                  <div className="mb-3">
                    <label className="form-label">
                      Symptoms during/after intercourse? <span className="text-danger">*</span>
                    </label>
                    <Select
                      isMulti
                      name="symptoms"
                      value={formData.symptoms}
                      onChange={(selected, action) => handleSelectChange(selected, { ...action, name: "symptoms" })}
                      options={symptomsOptions}
                      placeholder="Select symptoms"
                      isClearable
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "50px",
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: "4px",
                        }),
                        indicatorSeparator: (provided) => ({
                          ...provided,
                          display: "none",
                        }),
                      }}
                    />
                    {errors.symptoms && <small className="text-danger">{errors.symptoms}</small>}

                    {formData.symptoms?.some((option) => option.value === "Other") && (
                      <div className="mt-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Please specify other symptom"
                          name="otherSymptom"
                          value={formData.otherSymptom}
                          onChange={handleInputChange}
                        />
                        {errors.otherSymptom && <small className="text-danger">{errors.otherSymptom}</small>}
                      </div>
                    )}
                  </div>

                  {/* Metallic Color Dropdown */}
                  <div className="mb-3">
                    <label className="form-label">
                      Metallic color <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="metallicColor"
                      value={formData.metallicColor}
                      onChange={(selected, action) =>
                        handleSelectChange(selected, { ...action, name: "metallicColor" })
                      }
                      options={metallicColorOptions}
                      placeholder="Select color"
                      isClearable
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "50px",
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          padding: "4px",
                        }),
                        indicatorSeparator: (provided) => ({
                          ...provided,
                          display: "none",
                        }),
                      }}
                    />
                    {errors.metallicColor && <small className="text-danger">{errors.metallicColor}</small>}
                  </div>

                  {/* Footer */}
                  <div className="text-end">
                    {/* <Button type="submit" variant="primary" disabled={isSubmitting}>
                      {isSubmitting ? (isEditMode ? "Updating..." : "Saving...") : isEditMode ? "Update" : "Save"}
                    </Button> */}
                    <Button type="submit" variant="primary" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {isEditMode ? "Updating..." : "Saving..."}
                        </>
                      ) : (
                        isEditMode ? "Update" : "Save"
                      )}
                    </Button>
                  </div>
                </form>
              </Modal.Body>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PatientMale;
